import * as XLSX from "xlsx";
import { format } from "date-fns";

/** Must match table `rowKey`: order_id ?? id */
export function getOrderRowKey(order: {
  order_id?: string;
  id?: string;
}): string {
  return String(order.order_id ?? order.id);
}

function formatDateVal(v: unknown): string {
  if (v === null || v === undefined || v === "") {
    return "";
  }
  try {
    return format(new Date(v as string), "dd/MM/yyyy HH:mm");
  } catch {
    return String(v);
  }
}

/** One row per order — nested API keys flattened with dot notation in headers */
export function buildOrdersSheetRows(orders: any[]) {
  return orders.map(o => {
    const raw = o as Record<string, any>;
    const cd = raw.customer_detail ?? {};
    const da = raw.delivery_address_detail ?? {};
    const mmtc = raw.mmtcid ?? raw.mmtc_order_id;

    return {
      id: raw.id ?? "",
      order_id: raw.order_id ?? "",
      mmtcid: mmtc ?? "",
      pan_number: raw.pan_number ?? "",
      customer: raw.customer ?? "",
      order_date: formatDateVal(raw.order_date),
      grand_total: raw.grand_total ?? "",
      order_status: raw.order_status ?? "",
      payment_status: raw.payment_status ?? "",
      item_count:
        raw.item_count ?? (Array.isArray(raw.items) ? raw.items.length : ""),
      invoice: raw.invoice ?? "",
      invoice_url: raw.invoice_url ?? "",
      created_on: formatDateVal(raw.created_on),
      "customer_detail.id": cd.id ?? "",
      "customer_detail.email": cd.email ?? "",
      "customer_detail.first_name": cd.first_name ?? "",
      "customer_detail.last_name": cd.last_name ?? "",
      "customer_detail.full_name": cd.full_name ?? "",
      "customer_detail.phone_number": cd.phone_number ?? "",
      "customer_detail.gender": cd.gender ?? "",
      "customer_detail.date_of_birth": cd.date_of_birth ?? "",
      "customer_detail.is_profile_completed": cd.is_profile_completed ?? "",
      "customer_detail.is_active": cd.is_active ?? "",
      "delivery_address_detail.id": da.id ?? "",
      "delivery_address_detail.user": da.user ?? "",
      "delivery_address_detail.is_default": da.is_default ?? "",
      "delivery_address_detail.name": da.name ?? "",
      "delivery_address_detail.phone": da.phone ?? "",
      "delivery_address_detail.street_address_1": da.street_address_1 ?? "",
      "delivery_address_detail.street_address_2": da.street_address_2 ?? "",
      "delivery_address_detail.city": da.city ?? "",
      "delivery_address_detail.state": da.state ?? "",
      "delivery_address_detail.pincode": da.pincode ?? "",
      "delivery_address_detail.country": da.country ?? "",
      "delivery_address_detail.created_on": formatDateVal(da.created_on),
      "delivery_address_detail.updated_on": formatDateVal(da.updated_on),
    };
  });
}

/** One row per line item — links to parent order */
export function buildItemsSheetRows(orders: any[]) {
  const rows: Record<string, unknown>[] = [];

  for (const o of orders) {
    const raw = o as Record<string, any>;
    const orderIdDisplay = raw.order_id ?? raw.id ?? "";
    const items = raw.items;
    if (!Array.isArray(items) || items.length === 0) {
      rows.push({
        parent_order_id: raw.id ?? "",
        order_id: orderIdDisplay,
        item_index: "",
        id: "",
        product_name: "",
        sku: "",
        purity: "",
        weight_grams: "",
        quantity: "",
        unit_price: "",
        line_total: "",
        product_image: "",
      });
      continue;
    }

    items.forEach((item: Record<string, unknown>, idx: number) => {
      rows.push({
        parent_order_id: raw.id ?? "",
        order_id: orderIdDisplay,
        item_index: idx + 1,
        id: item.id ?? "",
        product_name: item.product_name ?? "",
        sku: item.sku ?? "",
        purity: item.purity ?? "",
        weight_grams: item.weight_grams ?? "",
        quantity: item.quantity ?? "",
        unit_price: item.unit_price ?? "",
        line_total: item.line_total ?? "",
        product_image: item.product_image ?? "",
      });
    });
  }

  return rows;
}

export function downloadGoldOrdersExcel(
  orders: any[],
  filename = "gold_orders"
) {
  const wb = XLSX.utils.book_new();

  const orderRows = buildOrdersSheetRows(orders);
  const ws1 = XLSX.utils.json_to_sheet(orderRows);
  XLSX.utils.book_append_sheet(wb, ws1, "Orders");

  const itemRows = buildItemsSheetRows(orders);
  const ws2 = XLSX.utils.json_to_sheet(itemRows);
  XLSX.utils.book_append_sheet(wb, ws2, "Order Items");

  const safeName = filename.replace(/[/\\?%*:|"<>]/g, "-");
  XLSX.writeFile(wb, `${safeName}.xlsx`);
}
